/**
 * Card — shared types, Zod prop schema, and fixed shell tokens.
 *
 * A presentational container for composing existing design-system components
 * inside consistent card chrome. Card owns the surface treatment; child layout
 * remains the responsibility of Stack, CardGrid, or other nested components.
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { radius } from '../../elements/radius';
import { SpacingKey, spacingValue } from '../Stack/Stack';
import {
  CARD_PADDING,
  CARD_RADIUS,
  resolveDarkCardColors,
  resolveLightCardColors,
  type CardColors,
} from '../_card/shared';

const CARD_BACKGROUND_NAMES = [
  'container',
  'dark',
  'inverse',
  'callout',
  'muted',
  'spotlight',
  'cream',
  'mistblue',
] as const;

export type CardBackground = (typeof CARD_BACKGROUND_NAMES)[number];

export const CARD_BACKGROUNDS = {
  container: resolveLightCardColors(colors.background.container),
  dark: resolveDarkCardColors(),
  inverse: resolveDarkCardColors(colors.cerulean['800']),
  // Border matched to the fill: the default divider grey reads as a darker ring
  // on this light-blue surface, which the design does not draw.
  callout: resolveLightCardColors(colors.background.callout, undefined, colors.background.callout),
  // A muted neutral panel (Figma `misc/variation-n`) for low-emphasis cards
  // like the "getting to know you" home tile.
  muted: resolveLightCardColors(colors.misc.variationN),
  // The brightest surface in the set — for the one card on a screen that should
  // pull the eye ahead of everything around it.
  // Border matched to the fill: the default divider grey is invisible on white
  // but reads as a grey ring on this saturated yellow, which the design does not
  // draw.
  spotlight: resolveLightCardColors(colors.yellow['400'], undefined, colors.yellow['400']),
  // The warm off-white the illustration tints already call `cream` — one name
  // for one value across both closed sets. Reads as a settled companion to a
  // saturated surface rather than a surface competing with it, which is why it
  // is the usual fill for a footer band. Border matched to the fill for the
  // same reason `spotlight` matches its own: the default divider grey draws a
  // ring on a tinted card that the design does not.
  cream: resolveLightCardColors(colors.coral['100'], undefined, colors.coral['100']),
  // The soft periwinkle surface (Figma `Background/Background-mistblue`) a
  // card uses for the one band that announces something is ready. Border
  // matched to the fill for the same reason as the tinted surfaces above.
  mistblue: resolveLightCardColors(
    colors.background.mistblue,
    undefined,
    colors.background.mistblue,
  ),
} as const satisfies Record<CardBackground, CardColors>;

export const CardBackgroundEnum = z.enum(CARD_BACKGROUND_NAMES);

/**
 * The corner radii a card may opt into, as radius token keys. `xl` is the
 * canonical card radius; `xxl` is the larger corner a featured card takes.
 */
export const CardRadiusEnum = z.enum(['xl', 'xxl']);
export type CardRadius = z.infer<typeof CardRadiusEnum>;

export const cardPropsSchema = z.object({
  background: CardBackgroundEnum.default('container'),
  /** Optional tokenized vertical inset; absent means the canonical card padding. */
  paddingVertical: SpacingKey.optional(),
  /**
   * Optional tokenized horizontal inset; absent means the canonical card
   * padding. Set it when a leading tile must sit closer to the card edge than
   * the body copy does.
   */
  paddingHorizontal: SpacingKey.optional(),
  /**
   * Override the bottom inset only, when a card's last row needs more room
   * beneath it than its top needs above. Mirrors Stack's per-axis padding
   * overrides; absent means the uniform `CARD_PADDING` applies.
   */
  paddingBottom: SpacingKey.optional(),
  /**
   * Renders the card's LAST child as a band along its foot: flush to the left,
   * right and bottom edges, filled with this background, sharing the card's
   * bottom radius. Every other child stays in the padded body above it.
   *
   * A single prop rather than a boolean plus a colour, because a band with no
   * fill of its own is indistinguishable from an ordinary last child — pairing
   * them would add a combination with nothing to render.
   *
   * A card whose only child is the band is entirely band; there is no body to
   * pad, and no empty inset appears above it.
   */
  footerBand: CardBackgroundEnum.optional(),
  /**
   * Fill the height of a column-direction parent, such as a `CardGrid` cell,
   * instead of hugging the content, so a card beside a taller one in a grid row
   * matches it. Absent: the card hugs its content.
   *
   * Precondition: the parent is a column whose height is already set, as a
   * `CardGrid` cell's is by its row. On native the card fills it with `flex: 1`
   * along the parent's main axis, so in a row-direction parent (a horizontal
   * `Stack`) it would grow in width instead; on web, where `CardGrid` is CSS
   * Grid, it uses `height: 100%`. Same precondition as `ListitemCard`'s
   * `fillHeight`.
   */
  fillHeight: z.boolean().optional(),
  /**
   * Optional corner radius token. Absent means the canonical card radius, so
   * every card that does not set it keeps today's shape; a build that predates
   * the option drops it and draws the canonical radius. A footer band follows
   * whichever radius the shell clips to.
   */
  radius: CardRadiusEnum.optional(),
  testID: z.string().optional(),
});

export type CardProps = z.infer<typeof cardPropsSchema>;

export { CARD_PADDING, CARD_RADIUS };

/** The card's corner radius — the opted-in token when set, the canonical radius otherwise. */
export function cardRadiusFor(cardRadius?: CardRadius): number {
  return cardRadius === undefined ? CARD_RADIUS : radius[cardRadius];
}

export function cardColorsFor(background: CardBackground = 'container'): CardColors {
  return CARD_BACKGROUNDS[background];
}

/** The card's bottom inset — the override when set, the uniform padding otherwise. */
export function cardPaddingBottom(
  paddingBottom?: SpacingKey,
  paddingVertical?: SpacingKey,
): number {
  if (paddingBottom !== undefined) return spacingValue(paddingBottom);
  return paddingVertical === undefined ? CARD_PADDING : spacingValue(paddingVertical);
}
